import { useState, useEffect } from "react";
import { Wifi, WifiOff, User, RefreshCw, FileText, CheckCircle2, XCircle, Loader2, CreditCard, Zap } from "lucide-react";
import api from "@/lib/api";

function TestButton({ label, icon: Icon, onClick, testId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await onClick();
      setResult(data);
    } catch (e) {
      setResult({ success: false, error: e.response?.data?.detail || e.message });
    }
    setLoading(false);
  };

  return (
    <div className="crm-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
            <Icon className="w-5 h-5" />
          </div>
          <span className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>{label}</span>
        </div>
        <button onClick={run} disabled={loading} className="crm-btn-primary text-sm" data-testid={`${testId}-btn`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {loading ? "Executando..." : "Executar"}
        </button>
      </div>

      {result && (
        <div className="rounded-xl p-4 text-sm" style={{
          background: result.success ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)",
          border: `1px solid ${result.success ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}`,
        }}>
          <div className="flex items-center gap-2 mb-2">
            {result.success ? (
              <><CheckCircle2 className="w-4 h-4" style={{ color: "#10b981" }} /><span className="font-bold" style={{ color: "#10b981" }}>Sucesso</span></>
            ) : (
              <><XCircle className="w-4 h-4" style={{ color: "#ef4444" }} /><span className="font-bold" style={{ color: "#ef4444" }}>Erro</span></>
            )}
          </div>
          <pre className="text-xs whitespace-pre-wrap overflow-auto max-h-40" style={{ color: "var(--crm-text-secondary)", fontFamily: "monospace" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function StripeSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stripe/config").then((r) => setConfig(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 crm-card animate-pulse" />)}</div>;

  return (
    <div data-testid="stripe-settings-page">
      <h1 className="crm-heading mb-8 flex items-center gap-2">
        <CreditCard className="w-6 h-6" style={{ color: "var(--crm-accent)" }} />
        Configuração Stripe
      </h1>

      {/* Config status */}
      <div className="crm-card p-6 mb-8" style={{ border: `2px solid ${config?.configured ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}` }}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: config?.configured ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)", color: config?.configured ? "#10b981" : "#ef4444" }}>
            {config?.configured ? <Wifi className="w-7 h-7" /> : <WifiOff className="w-7 h-7" />}
          </div>
          <div>
            <p className="text-lg font-bold" style={{ color: "var(--crm-text)" }}>
              {config?.configured ? "Stripe Configurado" : "Stripe Não Configurado"}
            </p>
            {config?.configured && (
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm" style={{ color: "var(--crm-text-muted)" }}>Key: <code style={{ color: "var(--crm-text-secondary)" }}>{config.key_prefix}</code></span>
                <span className={`crm-badge text-xs ${config.mode === "test" ? "crm-badge-orange" : "crm-badge-green"}`}>
                  {config.mode === "test" ? "MODO TESTE" : "MODO LIVE"}
                </span>
              </div>
            )}
            {!config?.configured && (
              <p className="text-sm mt-1" style={{ color: "var(--crm-text-muted)" }}>
                Adicione STRIPE_API_KEY no arquivo .env do backend para habilitar.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Test buttons */}
      <h2 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: "var(--crm-text-muted)" }}>Testes de Integração</h2>
      <div className="space-y-4">
        <TestButton
          label="Testar Conexão"
          icon={Wifi}
          onClick={() => api.get("/stripe/test/connection")}
          testId="stripe-test-connection"
        />
        <TestButton
          label="Criar Cliente Teste"
          icon={User}
          onClick={() => api.post("/stripe/test/customer")}
          testId="stripe-test-customer"
        />
        <TestButton
          label="Criar Assinatura Teste"
          icon={RefreshCw}
          onClick={() => api.post("/stripe/test/subscription")}
          testId="stripe-test-subscription"
        />
        <TestButton
          label="Buscar Faturas"
          icon={FileText}
          onClick={() => api.get("/stripe/test/invoices")}
          testId="stripe-test-invoices"
        />
      </div>
    </div>
  );
}
